import { useMutation, useQueryClient } from '@tanstack/react-query';
import { billingService, queryKeys } from '@/services';
import type { ManualAdjustmentWriteParams } from '@/services/billing/billing-service';

/**
 * Manual-adjustment mutations (LR-563): create / edit / delete / post-to-BO.
 * Each invalidates the whole adjustments branch on success so the list refetches.
 * The three that act on an EXISTING row also invalidate on a 409, because that
 * status means the cached row's posted-state is stale (edit/delete/post all guard
 * on it server-side); create has no such row, so it has nothing stale to refresh.
 */
const useAdjustmentInvalidation = () => {
  const queryClient = useQueryClient();
  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: queryKeys.billing.adjustments(),
    });
  return { invalidate };
};

export const useCreateAdjustment = () => {
  const { invalidate } = useAdjustmentInvalidation();
  return useMutation({
    mutationFn: (params: ManualAdjustmentWriteParams) =>
      billingService.createAdjustment(params),
    onSuccess: invalidate,
  });
};

export const useUpdateAdjustment = () => {
  const { invalidate } = useAdjustmentInvalidation();
  return useMutation({
    mutationFn: ({
      id,
      params,
    }: {
      id: number;
      params: ManualAdjustmentWriteParams;
    }) => billingService.updateAdjustment(id, params),
    onSuccess: invalidate,
    onError: (error) => {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 409) invalidate();
    },
  });
};

export const useDeleteAdjustment = () => {
  const { invalidate } = useAdjustmentInvalidation();
  return useMutation({
    mutationFn: (id: number) => billingService.deleteAdjustment(id),
    onSuccess: invalidate,
    onError: (error) => {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 409) invalidate();
    },
  });
};

export const usePostAdjustment = () => {
  const { invalidate } = useAdjustmentInvalidation();
  return useMutation({
    mutationFn: (id: number) => billingService.postAdjustment(id),
    onSuccess: invalidate,
    onError: (error) => {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      // 409 = already posted elsewhere: the cached "unposted" row is stale.
      if (status === 409) invalidate();
    },
  });
};
